import React from "react";
import PropTypes from "prop-types";
import { Card } from "semantic-ui-react";

import ManuscriptAddBlock from "./ManuscriptAddBlock";
import ManuscriptBlockMenu from "./ManuscriptBlockMenu";
import ManuscriptBlockType from "./ManuscriptBlockType";

import Blocks from "./BlockTypes";
import TextBlock from "./blocks/text/TextBlock";
import TeXBlock from "./blocks/text/TeXBlock";
import HeadingBlock from "./blocks/text/HeadingBlock";
import SpreadsheetBlock from "./blocks/spreadsheet/SpreadsheetBlock";
import MarkdownBlock from "./blocks/text/MarkdownBlock";
import ImageBlock from "./blocks/media/ImageBlock";
import VideoBlock from "./blocks/media/VideoBlock";
import ModelBlock from "./blocks/3d/ModelBlock";
import QuestionBlock from "./blocks/quiz/QuestionBlock";
import MultipleChoiceBlock from "./blocks/quiz/MultipleChoiceBlock";
import ColumnsBlock from "./blocks/layout/ColumnsBlock";
import NotesBlock from "./blocks/layout/NotesBlock";

/**
 * ManuscriptBlock: props => React element
 *
 * (c) Stefan Wittwer 2018 | Alle Rechte vorbehalten.
 *
 * Block which renders out manuscript content, depending
 * on its block type and renderer
 *
 * @param {*} props
 */
const ManuscriptBlock = props => {
  let renderer = null;

  /**
   * Switches on the block type to instanciate the
   * correct renderer
   */
  switch (props.block.type) {
    case Blocks.Text:
      renderer = <TextBlock {...props} />;
      break;
    case Blocks.LaTeX:
      renderer = <TeXBlock {...props} />;
      break;
    case Blocks.Heading:
      renderer = <HeadingBlock {...props} />;
      break;
    case Blocks.Spreadsheet:
      renderer = <SpreadsheetBlock {...props} />;
      break;
    case Blocks.Markdown:
      renderer = <MarkdownBlock {...props} />;
      break;
    case Blocks.Image:
      renderer = <ImageBlock {...props} />;
      break;
    case Blocks.Video:
      renderer = <VideoBlock {...props} />;
      break;
    case Blocks.Model:
      renderer = <ModelBlock {...props} />;
      break;
    case Blocks.Question:
      renderer = <QuestionBlock {...props} />;
      break;
    case Blocks.MultipleChoice:
      renderer = <MultipleChoiceBlock {...props} />;
      break;
    case Blocks.Columns:
      renderer = <ColumnsBlock {...props} />;
      break;
    case Blocks.Notes:
      renderer = <NotesBlock {...props} />;
      break;
    default:
      break;
  }

  /**
   * Renders the content block using the specified type renderer
   * and its surrounding controls
   */
  return (
    <div
      className={`ed-manuscript-block${
        props.isReadOnlyMode ? " render" : ""
      } ${props.block.type.replace(".", "-")}`}
    >
      <Card fluid>
        <Card.Content>
          {!props.isReadOnlyMode ? (
            <div>
              <ManuscriptBlockType type={props.block.type} />
              <ManuscriptBlockMenu
                onDelete={props.onDelete}
                onMove={props.onMove}
                canMoveUp={!props.isFirstElement}
                canMoveDown={!props.isLastElement}
              />
            </div>
          ) : null}
          <div className="ed-content-render">{renderer}</div>
        </Card.Content>
      </Card>
      {!props.isReadOnlyMode ? (
        <ManuscriptAddBlock onClick={props.onInsert} />
      ) : null}
    </div>
  );
};

ManuscriptBlock.propTypes = {
  /**
   * block: {type, text}
   * Manuscript content block containing its type
   * and passed on to the renderer
   */
  block: PropTypes.shape({
    type: PropTypes.string.isRequired
  }).isRequired,

  /**
   * onInsert: () => ()
   * Called when a manuscript block should be inserted around
   * this block
   */
  onInsert: PropTypes.func.isRequired,

  /**
   * onDelete: () => ()
   * Called when this manuscript block should be removed
   */
  onDelete: PropTypes.func.isRequired,

  /**
   * onMove: offset => ()
   * Called when this manuscript block should be moved to
   * another position
   */
  onMove: PropTypes.func.isRequired,

  /**
   * onChange: updatedBlock => ()
   * Fired when a block update has occurred and should be propagated
   * upward to the editor
   */
  onChange: PropTypes.func.isRequired,

  /**
   * isFirstElement: Boolean
   * True if the current block is the first sequential element
   */
  isFirstElement: PropTypes.bool,

  /**
   * isLastElement: Boolean
   * True if the current block is the last sequential element
   */
  isLastElement: PropTypes.bool,

  /**
   * isReadOnlyMode: Boolean
   * True if the current block should be read-only
   */
  isReadOnlyMode: PropTypes.bool
};

ManuscriptBlock.defaultProps = {
  block: {
    text: ""
  },
  isFirstElement: false,
  isLastElement: false,

  isReadOnlyMode: false
};

export default ManuscriptBlock;



// WEBPACK FOOTER //
// ./src/modules/editor/manuscript/ManuscriptBlock.js